<template>
  <div class="project-details">
    <header v-if="detailedProject" class="central-content primary-background">
      <div @click="goBackToProjects" class="button">
        <v-icon class="back-icon">arrow_back</v-icon>
        <div>{{ $t('projectDetails.back.to.project.list') }}</div>
      </div>
      <div class="progress-bar">
        <div class='progress-section' v-if="!isProjectStatusWithoutProgress">
          <span :class="{ ready: detailedProject.progress === 100 }" class="percent">
            {{ detailedProject.progress }}%
          </span>
          <project-spinner-progress v-if='detailedProject.progress != 100' class="circle" :progress='detailedProject.progress' :color="progressColor"/>
          <circle-progress v-else :progress="detailedProject.progress" :color="progressColor" class="circle"/>
        </div>
        <project-status :item="detailedProject" :show-finish-date="true" class="status-wrapper" :class='{ "progress": !isProjectStatusWithoutProgress }'/>
      </div>
      <div class="header-row">
        <div class="general-information-row">
          <div class="name">{{ formattedProjectName }}</div>
          <div class="number">{{ detailedProject.projectNumber }}</div>
        </div>
        <v-btn v-if="isAbleToFinishProject" class="finish-project" color="primary" @click="finishProject">
          {{ $t('finish') }}
        </v-btn>
      </div>
      <div class="type">{{ detailedProject.type && $t(`project.type.${detailedProject.type}`) }}</div>
    </header>
    <div v-else class="central-content primary-background loading-container">
      <v-progress-circular :size="70" :width="7" color="#4D9FEB" indeterminate />
    </div>
    <v-tabs>
      <v-tab
        v-for="tab in tabs"
        class="tab-heading"
        :class="{ disabled: isOnlyGeneralTabAllowed(tab) }"
        :key="tab"
        :to="{ name: tab }"
        :href="`#${tab}`"
        :disabled="isOnlyGeneralTabAllowed(tab)"
      >
        {{ $t(tab) }}
      </v-tab>
    </v-tabs>
    <v-card v-if="detailedProject" class="central-content tab-content" flat tile>
      <router-view></router-view>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { views } from '@/router/constants';
import { userRoleGroups } from '@/constants/auth';
import {
  TASK_STATUSES, projectStatuses, testTypes, ACTIVE_TEST_CARD_TYPES,
} from '@/constants/projects';
import { LOAD_DETAILED_PROJECT, CLEAR_DETAILED_PROJECT, UPDATE_PROJECT_STATUS } from '@/store/project/actionTypes';

import CircleProgress from '@/components/common/CircleProgress.vue';
import ProjectStatus from '@/components/ProjectDetails/ProjectStatus.vue';
import ProjectSpinnerProgress from '@/components/common/ProjectSpinnerProgress.vue';
import { CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS } from '@/store/centralizedIssues/actionTypes';

export default {
  name: 'ProjectDetails',
  components: {
    CircleProgress,
    ProjectStatus,
    ProjectSpinnerProgress,
  },
  props: {
    id: {
      required: true,
    },
  },
  data() {
    return {
      polling: null,
    };
  },
  computed: {
    ...mapGetters(['detailedProject', 'projectLoading', 'userRoleGroup', 'paginationItemsPerPageByTabs', 'projectIssuesRefId', 'projectIssuesProgress', 'projectIssuesRunningInterval']),
    formattedProjectName() {
      let formattedName = this.detailedProject.name;
      if (this.detailedProject.type === testTypes.AUTOMATIC && formattedName.includes('for ')) {
        const [typeSection, targetSection] = formattedName.split('for ');
        const formattedTypeSection = typeSection.trim().replaceAll(' ', '-').toLowerCase();
        formattedName = `${this.$t(`project.type.${formattedTypeSection}`)} for ${targetSection}`;
      }
      return formattedName;
    },
    isProjectStatusWithoutProgress() {
      return this.projectStatusesWithoutProgress.includes(this.detailedProject.status);
    },
    projectStatusesWithoutProgress() {
      return [projectStatuses.CANCELED, projectStatuses.FAILED, projectStatuses.PENDING];
    },
    issuesTabKey() {
      return this.isUserSP ? views.PROJECT_FINDINGS : views.PROJECT_ISSUES;
    },
    automaticIssuesTabKey() {
      return this.isUserSP ? views.PROJECT_FINDINGS : views.AUTOMATIC_PROJECT_ISSUES;
    },
    isUserSP() {
      return this.userRoleGroup === userRoleGroups.SP;
    },
    progressColor() {
      if (this.detailedProject.progress === 100) {
        return getComputedStyle(document.documentElement).getPropertyValue('--status-info').trim();
      }
      return '#56C228';
    },
    isAbleToFinishProject() {
      return (
        this.isUserSP &&
        this.detailedProject?.status !== projectStatuses.FINISHED &&
        this.detailedProject.resources.every(resource => resource?.task?.status === TASK_STATUSES.finished)
      );
    },
    isProjectAutomatic() {
      return this.detailedProject?.type === testTypes.AUTOMATIC;
    },
    activeTestCardTypes() {
      return Object.values(ACTIVE_TEST_CARD_TYPES);
    },
    isProjectActive() {
      return (this.isProjectAutomatic && this.activeTestCardTypes.includes(this.detailedProject.testCard?.type)) || false;
    },
    tabsForActiveProject() {
      return [
        this.automaticIssuesTabKey,
        views.PROJECT_TECHNICAL_INFORMATION,
        views.PROJECT_GENERAL_INFORMATION,
      ];
    },
    tabsForAutomaticProject() {
      return [
        this.automaticIssuesTabKey,
        views.PROJECT_TECHNICAL_INFORMATION,
        views.PROJECT_GENERAL_INFORMATION,
      ];
    },
    tabsForPTProject() {
      return [
        views.PROJECT_GENERAL_INFORMATION,
        views.PROJECT_TECHNICAL_INFORMATION,
        views.PROJECT_WORK_WITH_PROJECT,
        this.issuesTabKey,
      ];
    },
    tabs() {
      let tabs = [];
      if (this.detailedProject) {
        if (this.isProjectAutomatic) {
          if (this.isProjectActive) {
            tabs = this.tabsForActiveProject;
          } else {
            tabs = this.tabsForAutomaticProject;
          }
        } else {
          tabs = this.tabsForPTProject;
        }
      }
      return tabs;
    },
  },
  async mounted() {
    await this.$store.dispatch(LOAD_DETAILED_PROJECT, this.id);
    await this.loadIssues({sort: {priorityScore: 'desc'}});
  },
  async destroyed() {
    await this.$store.dispatch(CLEAR_DETAILED_PROJECT);
    await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
      page: 1,
      limit: this.paginationItemsPerPageByTabs.centralizedIssues,
      hideSuppress: true,
      onlyCVEs: false,
      hideFixed: true,
      specificSearch: false,
      search: '',
      issueTypes: [],
      sort: {
        priorityScore: 'desc',
      },
    });
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  methods: {
    async loadIssues(payload = {}, turnOnLoading = true) {
      this.clearInterval();
      await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
        page: 1,
        limit: this.paginationItemsPerPageByTabs.centralizedIssues,
        projectId: this.id,
        turnOnLoading,
        hideSuppress: true,
        onlyCVEs: false,
        hideFixed: true,
        search: '',
        issueTypes: [],
        ...payload,
      });
      if (this.projectIssuesRefId && this.projectIssuesRefId !== '') {
        this.polling = setInterval(async () => {
          if (!this.projectIssuesRefId || this.projectIssuesRefId === '') {
            this.clearInterval();
          } else if (!this.projectIssuesRunningInterval) {
            await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
              page: 1,
              limit: this.paginationItemsPerPageByTabs.centralizedIssues,
              projectId: this.id,
              turnOnLoading,
              hideSuppress: true,
              onlyCVEs: false,
              hideFixed: true,
              search: '',
              issueTypes: [],
              refId: this.projectIssuesRefId,
              ...payload,
            });
          }
        }, 3000);
      }
    },
    clearInterval() {
      clearInterval(this.polling);
      this.polling = null;
    },
    goBackToProjects() {
      this.$router.push({ name: views.ACTIVITY_LOG });
    },
    async finishProject() {
      await this.$store.dispatch(UPDATE_PROJECT_STATUS, {
        projectId: this.detailedProject.id,
        status: projectStatuses.FINISHED,
      });
      await this.$store.dispatch(LOAD_DETAILED_PROJECT, this.id);
      this.$router.replace({ name: views.PROJECT_GENERAL_INFORMATION });
    },

    isOnlyGeneralTabAllowed(tabName) {
      return (
        this.isUserSP &&
        tabName !== 'projectDetails.tab.general' &&
        this.detailedProject?.status !== projectStatuses.IN_PROGRESS
      );
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';
.project-status {
  &.status-wrapper {
    .status {
      padding: 0 rem(8);
    }
  }
}

.loading-container {
  padding: rem(40);
  display: flex;
  justify-content: center;
  align-items: center;
}

.project-details .v-slide-group__wrapper {
  padding-left: rem(120);
  background-color: var(--bg-primary);
}

[data-theme="dark"] .project-details .v-slide-group__wrapper {
  background-color: var(--bg-secondary);
}

.project-details [role="tablist"] {
  background-color: var(--bg-primary) !important;
}

[data-theme="dark"] .project-details [role="tablist"] {
  background-color: var(--bg-secondary) !important;
}

.v-window.v-item-group.v-tabs-items {
  box-shadow: 0 rem(-2) rem(4) 0 rgba(14, 31, 51, 0.13);
}

.project-details .tab-content.v-card.v-card {
  background-color: var(--bg-primary) !important;
  box-shadow: none;
}

// In dark mode, match all backgrounds to bg-secondary (dark blue)
[data-theme="dark"] .project-details .tab-content.v-card.v-card {
  background-color: var(--bg-secondary) !important;
}

[data-theme="dark"] .project-details {
  background: var(--bg-secondary);
}

// Match finished progress circle color to status-info in both themes
.project-details .progress-bar .circle-progress .v-progress-circular {
  color: var(--status-info) !important;
}
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.project-details {
  overflow: hidden;
  min-height: 100vh;
}

.progress-bar {
  display: flex;
  align-items: center;
  margin-bottom: rem(8);

  .circle {
    width: rem(22);
    height: rem(22);
    margin-left: rem(3);
  }

  .percent {
    display: inline-block;
    color: var(--status-success);
    font-size: rem(14);
    font-weight: 600;
    letter-spacing: 0;
    text-align: center;
  }

  .ready {
    color: var(--status-info);
  }

  .progress-section {
    display: flex;
    align-items: center;
    margin-bottom: rem(4);
  }

  .status-wrapper {
    max-height: rem(20);
    display: flex;
    justify-content: center;
    align-items: center;
    &.progress {
      margin-left: rem(12);
    }
  }
}

.loading-container {
  padding: rem(40);
  display: flex;
  justify-content: center;
  align-items: center;
}

.primary-background {
  background-color: var(--bg-primary);
}

[data-theme="dark"] .primary-background {
  background-color: var(--bg-secondary);
}

.central-content {
  padding-left: rem(120);
  padding-right: rem(120);
}

header {
  padding: rem(16) 0 rem(24) 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .general-information-row {
    display: flex;
  }
}
.tab-heading {
  text-transform: capitalize;
  opacity: 0.5;
  color: var(--text-primary);
  font-family: $font-family-medium;
  font-size: rem(16);
  font-weight: 500;
  letter-spacing: 0;
  line-height: rem(20);
}

.name {
  color: var(--text-primary);
  font-family: $font-family-bold;
  font-size: rem(24);
  font-weight: bold;
  letter-spacing: 0;
  line-height: rem(28);
  margin-right: rem(16);
}

.number {
  color: var(--text-tertiary);
  font-family: $font-family-semibold;
  font-size: rem(24);
  font-weight: bold;
  letter-spacing: 0;
  line-height: rem(28);
}

.type {
  color: var(--text-tertiary);
  font-family: $font-family-regular;
  font-size: rem(14);
  letter-spacing: 0;
  line-height: rem(20);
}

.button {
  display: flex;
  align-items: center;
  color: var(--brand-primary);
  cursor: pointer;
  font-family: $font-family-medium;
  font-size: rem(12);
  font-weight: 500;
  letter-spacing: rem(1.07);
  line-height: rem(16);
  text-align: center;
  text-transform: uppercase;
  margin-bottom: rem(40);
}

.back-icon {
  margin-right: rem(6);
  color: var(--brand-primary);
}
</style>
